import {Controller} from "stimulus"

/*
 * Narrows a form's GL account selects to the accounts flagged
 * data-default-list="true" (the budgeted ones) until the "Show all active
 * accounts" toggle is checked. The prompt and each select's current choice are
 * always kept. Removed options are held per select and put back in their
 * original order, and slim-select re-renders on its own because it watches
 * its select for child changes.
 *
 * Bill line rows and the new-bill fields arrive after connect (a turbo stream
 * and a turbo frame), so added account selects and toggles are picked up from
 * a MutationObserver. With no toggle on the form every account is offered.
 * When the user flips the toggle, the change is written to the status target
 * (a role="status" live region) so screen readers hear that the lists changed.
 *
 *   <form data-controller="budgeted-accounts">
 *     <input type="checkbox" data-target="budgeted-accounts.toggle"
 *            data-action="change->budgeted-accounts#toggle">
 *     <p role="status" aria-live="polite" data-target="budgeted-accounts.status"></p>
 *     <select data-target="budgeted-accounts.select">...</select>
 */
export default class extends Controller {
  static targets = ["toggle", "select", "status"]

  connect() {
    this.allOptions = new WeakMap()
    this.applyToAll()
    this.observer = new MutationObserver(mutations => {
      if (mutations.some(mutation => this.addsTargets(mutation))) this.applyToAll()
    })
    this.observer.observe(this.element, {childList: true, subtree: true})
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  toggle() {
    this.applyToAll()
    this.announce()
  }

  announce() {
    const message = this.showAll()
      ? "Showing all active accounts."
      : "Showing budgeted expense accounts and liability accounts only."
    this.statusTargets.forEach(status => { status.textContent = message })
  }

  applyToAll() {
    const showAll = this.showAll()
    this.selectTargets.forEach(select => this.apply(select, showAll))
  }

  apply(select, showAll) {
    const all = this.originalOptions(select)
    const value = select.value
    const wanted = all.filter(option => showAll || this.keep(option, value))
    if (this.sameOptions(select, wanted)) return

    select.replaceChildren(...wanted)
    select.value = value
  }

  keep(option, value) {
    return option.value === "" || option.value === value || option.dataset.defaultList === "true"
  }

  showAll() {
    return this.toggleTargets.length === 0 || this.toggleTargets.some(toggle => toggle.checked)
  }

  originalOptions(select) {
    if (!this.allOptions.has(select)) this.allOptions.set(select, Array.from(select.options))
    return this.allOptions.get(select)
  }

  sameOptions(select, wanted) {
    const current = Array.from(select.options)
    return current.length === wanted.length && current.every((option, index) => option === wanted[index])
  }

  addsTargets(mutation) {
    const selector = "[data-target~='budgeted-accounts.select'], [data-target~='budgeted-accounts.toggle']"
    return Array.from(mutation.addedNodes).some(node =>
      node.nodeType === Node.ELEMENT_NODE && (node.matches(selector) || node.querySelector(selector))
    )
  }
}
